/**
 * npc/npcs.ts — NPC Definitions and Spawning
 *
 * Every zone in Hearth has characters who live there. They have names,
 * opinions, wardrobes, and things to say. They make the world feel like
 * someone was here before you arrived — and will be here after you leave.
 */

import type { NpcState, CharacterAppearance, Direction, AnimState, ShopItem } from "../engine/state.js";

// ─── Helpers ────────────────────────────────────────────────────────────────

function npc(
  id: string,
  name: string,
  title: string,
  appearance: CharacterAppearance,
  position: { tileX: number; tileY: number; elevation: number },
  direction: Direction,
  role: NpcState["role"],
  zoneId: string,
  dialogue: string[],
  shopItems?: ShopItem[],
): NpcState {
  return {
    id,
    name,
    title,
    appearance,
    position,
    direction,
    animState: "idle" as AnimState,
    role,
    zoneId,
    gazeAngle: 0,
    gazeFocus: 0,
    dialogue,
    dialogueIndex: 0,
    inConversation: false,
    shopItems,
  };
}

// ─── Zone NPC Registries ────────────────────────────────────────────────────

// Ember Lane — 12x10, cobblestone center (x:4-7), wood sides
// Home zone. Warm. Safe. Nana Mae sits near a lamp on the quiet side.

function emberLaneNpcs(): NpcState[] {
  return [
    npc(
      "npc_nana_mae", "Nana Mae", "Village Elder",
      {
        skinTone: "#c8906a",
        hairStyle: "short_curly",
        hairColour: "#d0d0d0",
        outfitTop: "cozy_sweater",
        outfitTopColour: "#8b5e3c",
        outfitBottom: "simple_pants",
        outfitBottomColour: "#5a4a3a",
        shoes: "simple_boots",
        shoesColour: "#3a2a1a",
        faceStyle: "kind",
      },
      { tileX: 9, tileY: 3, elevation: 0 },
      "SW",
      "guide",
      "ember_lane",
      [
        "Welcome home, dear. The kettle's always warm on Ember Lane.",
        "I've lived here sixty years. The lamplighters still come every evening, same as always.",
        "If you're heading out past the square, bring a warm coat. The harbor wind bites.",
        "The old tree in Verdant Park? It remembers things. You should visit sometime.",
        "Home isn't the walls, love. It's the feeling you get when the lamp is lit and someone's waiting.",
      ],
    ),
  ];
}

// Hearthstone Square — 14x14, marble center, cobblestone ring, fountain at (5-6, 5-6)
// Town center. Bustling. Callum stands near the fountain, Thessa near a bench.

function hearthstoneSquareNpcs(): NpcState[] {
  return [
    npc(
      "npc_callum", "Callum", "Town Crier",
      {
        skinTone: "#a0704a",
        hairStyle: "short_spiky",
        hairColour: "#1a1a2a",
        outfitTop: "tunic",
        outfitTopColour: "#c04040",
        outfitBottom: "trousers",
        outfitBottomColour: "#3a3a4a",
        shoes: "simple_boots",
        shoesColour: "#2a1a0a",
        faceStyle: "friendly",
      },
      { tileX: 8, tileY: 7, elevation: 1 },
      "W",
      "guide",
      "hearthstone_square",
      [
        "Hear ye! Welcome to Hearthstone Square, heart of the town! The fountain never freezes, not even in the deepest winter.",
        "Tip for newcomers: visit Pim at the Lantern Market before dark. Best prices in the morning, best stories at night.",
        "The Ironforge District is hiring apprentices. If you can stand the heat, Torva will teach you well.",
        "They say Professor Aelith saw something strange from Skyreach last week. Stars moving wrong, or something.",
      ],
    ),
    npc(
      "npc_thessa", "Thessa", "Artisan",
      {
        skinTone: "#d4a07a",
        hairStyle: "long_braid",
        hairColour: "#8b4513",
        outfitTop: "apron",
        outfitTopColour: "#6a8a5a",
        outfitBottom: "skirt",
        outfitBottomColour: "#d4c4a4",
        shoes: "sandals",
        shoesColour: "#5a4030",
        faceStyle: "friendly",
      },
      { tileX: 3, tileY: 9, elevation: 0 },
      "E",
      "quest_giver",
      "hearthstone_square",
      [
        "Oh, hello! I'm working on a new mosaic for the fountain. Do you like tilework?",
        "I need certain pigments — crushed crystal from Roothollow makes the most extraordinary blue.",
        "My grandmother laid the first stones of this square. Every tile has a name scratched underneath.",
        "If you find any unusual materials on your travels, bring them to me. I can make something beautiful.",
      ],
    ),
  ];
}

// Lantern Market — 14x12, marble walkway center (x:5-8), shop walls N and W
// Busy market. Pim near the stalls on the west side, Zara deeper in with exotic wares.

function lanternMarketNpcs(): NpcState[] {
  return [
    npc(
      "npc_pim", "Pim", "General Merchant",
      {
        skinTone: "#e0b090",
        hairStyle: "medium_loose",
        hairColour: "#d4a040",
        outfitTop: "tunic",
        outfitTopColour: "#e08030",
        outfitBottom: "simple_pants",
        outfitBottomColour: "#5a4a2a",
        shoes: "simple_boots",
        shoesColour: "#4a3020",
        faceStyle: "friendly",
      },
      { tileX: 3, tileY: 3, elevation: 1 },
      "SE",
      "merchant",
      "lantern_market",
      [
        "Welcome, welcome! Pim's the name, fair prices are the game. Have a look around!",
        "Every lantern in this market I hung myself. Took three days and a very tall ladder.",
        "I get fresh stock from the harbor every week. Captain Rowe's a reliable sort.",
        "Looking for something special? My friend Zara has the exotic stuff — just past the rug display.",
      ],
      [
        { name: "Cozy Armchair",   furnitureTypeId: "sofa",        price: 120, rarity: "common",   description: "Sink in and never want to get up." },
        { name: "Oak Bookshelf",   furnitureTypeId: "bookshelf",   price: 200, rarity: "common",   description: "Holds exactly as many books as you'll never finish." },
        { name: "Woven Rug",       furnitureTypeId: "rug_round",   price: 80,  rarity: "common",   description: "Soft underfoot, warm in color." },
        { name: "Iron Floor Lamp", furnitureTypeId: "floor_lamp",  price: 150, rarity: "common",   description: "Casts a warm amber glow. Adjustable brightness." },
        { name: "Writing Desk",    furnitureTypeId: "desk",        price: 250, rarity: "uncommon", description: "Every great story started at a desk like this." },
        { name: "Potted Fern",     furnitureTypeId: "potted_fern", price: 60,  rarity: "common",   description: "Low maintenance. High coziness." },
      ],
    ),
    npc(
      "npc_zara", "Zara", "Exotic Trader",
      {
        skinTone: "#7a5a3a",
        hairStyle: "long_braid",
        hairColour: "#0a0a1a",
        outfitTop: "tunic",
        outfitTopColour: "#6030a0",
        outfitBottom: "skirt",
        outfitBottomColour: "#2a1a4a",
        shoes: "sandals",
        shoesColour: "#c09050",
        faceStyle: "stern",
      },
      { tileX: 11, tileY: 4, elevation: 0 },
      "W",
      "merchant",
      "lantern_market",
      [
        "I don't sell to just anyone. But you... you have the look of someone who appreciates rare things.",
        "This crystal ball came from deep below Roothollow. The miner who found it swore it hummed at night.",
        "I sailed with Captain Rowe for three years before I settled here. The sea teaches you what has real value.",
        "Everything I sell has a story. The price includes the story. That's non-negotiable.",
      ],
      [
        { name: "Crystal Ball",       furnitureTypeId: "crystal_ball",  price: 800,  rarity: "rare",      description: "Swirls with inner light. Some say it shows the future. It doesn't. But it's beautiful." },
        { name: "Moonlit Lantern",    furnitureTypeId: "floor_lamp",    price: 500,  rarity: "uncommon",  description: "Glows faintly blue. Harvested from the harbor on a full moon." },
        { name: "Whispering Fern",    furnitureTypeId: "potted_fern",   price: 350,  rarity: "uncommon",  description: "Its leaves rustle even when there's no breeze. From the Whisperwood." },
        { name: "Stormglass Candle",  furnitureTypeId: "candle",        price: 1200, rarity: "rare",      description: "The flame turns violet before a storm. Never goes out." },
        { name: "Ashen Stone Table",  furnitureTypeId: "desk",          price: 2500, rarity: "legendary", description: "Carved from a single boulder at the summit of Ashen Peaks. Weighs a ton. Worth ten." },
      ],
    ),
  ];
}

// Verdant Park — 16x16, grass with winding dirt path, pond at (10-13, 10-13)
// Peaceful. Moss tends the garden near the path.

function verdantParkNpcs(): NpcState[] {
  return [
    npc(
      "npc_moss", "Moss", "Park Keeper",
      {
        skinTone: "#b08060",
        hairStyle: "medium_loose",
        hairColour: "#4a6a3a",
        outfitTop: "tunic",
        outfitTopColour: "#5a7a4a",
        outfitBottom: "simple_pants",
        outfitBottomColour: "#6a5a3a",
        shoes: "sandals",
        shoesColour: "#3a3020",
        faceStyle: "kind",
      },
      { tileX: 7, tileY: 6, elevation: 0 },
      "S",
      "wanderer",
      "verdant_park",
      [
        "Shh... listen. The park is talking. You just have to slow down enough to hear it.",
        "I planted that oak thirty years ago. It was the size of my arm. Now I can't reach around it.",
        "The pond has three kinds of fish. I've named them all. Don't ask me to pick a favorite.",
        "Some people come here to think. Some come here to stop thinking. Both are good reasons.",
        "The Whisperwood starts where the park ends. The trees there are... different. Older. They watch.",
      ],
    ),
  ];
}

// Ironforge District — 12x12, stone_dark with lava at (5-6, 3-4), forge and workbenches
// Industrial warmth. Torva at the forge, Spark near a workbench.

function ironforgeDistrictNpcs(): NpcState[] {
  return [
    npc(
      "npc_torva", "Torva", "Master Blacksmith",
      {
        skinTone: "#8a6040",
        hairStyle: "short_spiky",
        hairColour: "#1a1a1a",
        outfitTop: "apron",
        outfitTopColour: "#4a3020",
        outfitBottom: "trousers",
        outfitBottomColour: "#2a2a2a",
        shoes: "simple_boots",
        shoesColour: "#1a1a1a",
        faceStyle: "stern",
      },
      { tileX: 8, tileY: 4, elevation: 0 },
      "W",
      "merchant",
      "ironforge_district",
      [
        "The forge doesn't care about your feelings. It cares about heat, timing, and patience. Same as life.",
        "I've been hammering since before you were born. Every ring of the anvil is a conversation.",
        "Need something made? I work in iron, steel, and stubbornness. Bring materials and I'll shape them.",
        "Spark's got talent, I'll admit. Doesn't have the patience yet. But that comes with burns.",
      ],
      [
        { name: "Iron Candlestick",  furnitureTypeId: "candle",       price: 100, rarity: "common",   description: "Simple, sturdy, and makes a room feel warmer." },
        { name: "Wrought Iron Lamp", furnitureTypeId: "floor_lamp",   price: 300, rarity: "uncommon", description: "Torva's signature twist design. Built to last three lifetimes." },
        { name: "Forged Bookends",   furnitureTypeId: "bookshelf",    price: 180, rarity: "common",   description: "Shaped like tiny anvils. Hold any book upright." },
        { name: "Steel Tool Rack",   furnitureTypeId: "desk",         price: 220, rarity: "common",   description: "Every tool has its place. This rack remembers." },
        { name: "Ember Brazier",     furnitureTypeId: "fireplace",    price: 900, rarity: "rare",     description: "A portable forge-heart. Warms any room with the scent of hot metal." },
      ],
    ),
    npc(
      "npc_spark", "Spark", "Forge Apprentice",
      {
        skinTone: "#d4b090",
        hairStyle: "short_spiky",
        hairColour: "#c04010",
        outfitTop: "apron",
        outfitTopColour: "#6a5040",
        outfitBottom: "simple_pants",
        outfitBottomColour: "#4a4a4a",
        shoes: "simple_boots",
        shoesColour: "#2a2020",
        faceStyle: "friendly",
      },
      { tileX: 4, tileY: 8, elevation: 0 },
      "N",
      "quest_giver",
      "ironforge_district",
      [
        "Ow! Sorry, burned myself again. Torva says that's how you learn. I'm learning a LOT.",
        "I'm trying to make a perfect circle. Sounds easy, right? I've been at it for two weeks.",
        "Could you help me find some materials? I want to surprise Torva with something I made on my own.",
        "The lava channel keeps the forge hot all day. Nobody knows where it comes from. Deep, I guess.",
        "One day I'll be as good as Torva. Maybe better. Don't tell her I said that.",
      ],
    ),
  ];
}

// Moonlit Harbor — 14x12, water top (y:0-3), dock (y:4-5), stone wharf below
// Salt air and lantern light. Captain Rowe on the dock, Old Salty near the cargo.

function moonlitHarborNpcs(): NpcState[] {
  return [
    npc(
      "npc_captain_rowe", "Captain Rowe", "Dockmaster",
      {
        skinTone: "#b07050",
        hairStyle: "medium_loose",
        hairColour: "#4a4a5a",
        outfitTop: "tunic",
        outfitTopColour: "#2a3a5a",
        outfitBottom: "trousers",
        outfitBottomColour: "#1a1a2a",
        shoes: "simple_boots",
        shoesColour: "#1a1010",
        faceStyle: "stern",
      },
      { tileX: 7, tileY: 5, elevation: 2 },
      "S",
      "guide",
      "moonlit_harbor",
      [
        "The tide's turning. You can tell by the way the ropes creak. Twenty years of listening teaches you that.",
        "I run a clean harbor. Ships come in, ships go out. Nobody gets cheated on my dock.",
        "Stormwatch Cliffs are no joke. I lost a good ship there in '08. The lighthouse is the only mercy that coast has.",
        "Zara used to be my first mate. Best eyes on the crew. She reads the sea like a book.",
        "If you're heading north along the coast, watch the sky. Storm rolls in fast out there.",
      ],
    ),
    npc(
      "npc_old_salty", "Old Salty", "Fisher",
      {
        skinTone: "#c09070",
        hairStyle: "none",
        hairColour: "#808080",
        outfitTop: "tunic",
        outfitTopColour: "#7a8a6a",
        outfitBottom: "simple_pants",
        outfitBottomColour: "#5a5a4a",
        shoes: "simple_boots",
        shoesColour: "#4a3a2a",
        faceStyle: "kind",
      },
      { tileX: 2, tileY: 8, elevation: 2 },
      "E",
      "quest_giver",
      "moonlit_harbor",
      [
        "Fish aren't biting today. Never do when the moon's like that. Tomorrow, though? Tomorrow's a good moon.",
        "I've been fishing this harbor since before the dock was stone. Used to be all rope and prayer.",
        "There's something big in the deep water. I've hooked it twice. Both times it took my line and my dignity.",
        "Bring me something shiny — a crystal, a polished stone. Fish love shiny things. So do I, actually.",
      ],
    ),
  ];
}

// Whisperwood — 18x18, grass with dirt path winding through, trees everywhere, stream at y:12-15
// Dark and mysterious. Wren guards the path, Thistledown hides in the deep woods.

function whisperwoodNpcs(): NpcState[] {
  return [
    npc(
      "npc_wren", "Wren", "Forest Ranger",
      {
        skinTone: "#b8886a",
        hairStyle: "short_spiky",
        hairColour: "#3a2a1a",
        outfitTop: "tunic",
        outfitTopColour: "#4a5a3a",
        outfitBottom: "trousers",
        outfitBottomColour: "#3a3a2a",
        shoes: "simple_boots",
        shoesColour: "#2a2010",
        faceStyle: "stern",
      },
      { tileX: 9, tileY: 3, elevation: 0 },
      "S",
      "guard",
      "whisperwood",
      [
        "Stay on the path. I mean it. The trees rearrange themselves when you're not looking.",
        "I patrol from dawn to dusk. After dark, the forest belongs to itself.",
        "The crystal caverns are deeper in, past the stream. I wouldn't go there without a good reason.",
        "Thistledown? The hermit? She's harmless. Talks to mushrooms. They might talk back, for all I know.",
        "If you hear whispering and there's nobody around, walk faster. Don't answer.",
      ],
    ),
    npc(
      "npc_thistledown", "Thistledown", "Forest Hermit",
      {
        skinTone: "#d0a878",
        hairStyle: "long_braid",
        hairColour: "#90a070",
        outfitTop: "tunic",
        outfitTopColour: "#7a6a50",
        outfitBottom: "skirt",
        outfitBottomColour: "#5a6a4a",
        shoes: "sandals",
        shoesColour: "#4a3a2a",
        faceStyle: "kind",
      },
      { tileX: 3, tileY: 10, elevation: 0 },
      "E",
      "wanderer",
      "whisperwood",
      [
        "Oh! A visitor. The mushrooms said someone was coming, but I thought they were being dramatic.",
        "I've lived here seven years. The trees know my name now. They're slow learners, but loyal friends.",
        "The stream runs from Roothollow up through here. Sometimes it carries crystals. Beautiful little things.",
        "People think I'm strange for living out here. But have you seen a sunrise through the canopy? Nothing strange about choosing that.",
      ],
    ),
  ];
}

// Skyreach Terrace — 14x12, marble with elevation (y<3: elev 2, y<6: elev 1)
// High above the clouds. Professor Aelith studies the stars from the upper terrace.

function skyreachTerraceNpcs(): NpcState[] {
  return [
    npc(
      "npc_professor_aelith", "Professor Aelith", "Astronomer",
      {
        skinTone: "#c8a888",
        hairStyle: "medium_loose",
        hairColour: "#e0d0c0",
        outfitTop: "tunic",
        outfitTopColour: "#2a3060",
        outfitBottom: "trousers",
        outfitBottomColour: "#1a1a3a",
        shoes: "simple_boots",
        shoesColour: "#2a2030",
        faceStyle: "kind",
      },
      { tileX: 5, tileY: 2, elevation: 3 },
      "SE",
      "guide",
      "skyreach_terrace",
      [
        "Ah, another visitor! Come, look through the lens. On a clear night you can see the constellation of the Hearth — fitting, isn't it?",
        "I've been mapping the sky for thirty years. It keeps changing on me. Stars shouldn't do that.",
        "The wind up here carries sounds from the valleys below. Last Tuesday I heard Torva's anvil from here.",
        "Between you and me, the Stormwatch Cliffs lighthouse sends signals at night. Patterns. I'm still decoding them.",
        "Knowledge is warmth, my friend. A different kind than the forge or the fireplace, but warmth all the same.",
      ],
    ),
  ];
}

// Ashen Peaks — 16x16, stone with snow at high elevation, dirt path at base (y:13-14)
// Harsh but honest. Kael watches the trail at the mountain base.

function ashenPeaksNpcs(): NpcState[] {
  return [
    npc(
      "npc_kael", "Kael", "Mountain Guide",
      {
        skinTone: "#9a7050",
        hairStyle: "short_spiky",
        hairColour: "#1a1a1a",
        outfitTop: "cozy_sweater",
        outfitTopColour: "#5a5a6a",
        outfitBottom: "trousers",
        outfitBottomColour: "#3a3a3a",
        shoes: "simple_boots",
        shoesColour: "#1a1a10",
        faceStyle: "stern",
      },
      { tileX: 10, tileY: 13, elevation: 0 },
      "N",
      "guard",
      "ashen_peaks",
      [
        "The mountain doesn't care who you are. Respect it or it'll send you back down the hard way.",
        "I've guided people up these peaks for fifteen years. Half of them thanked me. The other half couldn't feel their toes.",
        "There's a summit shrine up top. Nobody remembers who built it or why. The snow never covers it, though. Strange.",
        "If the wind picks up, find shelter fast. I've seen it knock grown folk off their feet up here.",
        "Bring water. Bring warmth. Leave your pride at the base. The Peaks will humble you either way.",
      ],
    ),
  ];
}

// Roothollow — 14x14, stone_dark with crystal clusters, underground pool at (9-10, 9-10)
// Deep, quiet, glowing. Glimmer works near the crystals with her trade goods.

function roothollowNpcs(): NpcState[] {
  return [
    npc(
      "npc_glimmer", "Glimmer", "Crystal Miner",
      {
        skinTone: "#c0a080",
        hairStyle: "short_curly",
        hairColour: "#b0c0d0",
        outfitTop: "apron",
        outfitTopColour: "#5050a0",
        outfitBottom: "simple_pants",
        outfitBottomColour: "#3a3a5a",
        shoes: "simple_boots",
        shoesColour: "#2a2a3a",
        faceStyle: "friendly",
      },
      { tileX: 6, tileY: 6, elevation: 0 },
      "S",
      "merchant",
      "roothollow",
      [
        "Mind your step — the crystals are sharp and the floor is uneven. But isn't it beautiful down here?",
        "I've been mining these caves for a decade. Every crystal is different. Like snowflakes, but they last forever.",
        "The deeper tunnels lead to the Crystal Caverns. I don't go there anymore. Too dangerous, even for me.",
        "Some of these crystals sing if you hold them right. A low hum, like the earth remembering something.",
      ],
      [
        { name: "Luminous Crystal",   price: 400, rarity: "uncommon", description: "Glows faintly in the dark. Perfect for a nightstand." },
        { name: "Amethyst Cluster",    price: 600, rarity: "uncommon", description: "A natural formation of deep purple crystals. Each one unique." },
        { name: "Crystal Lamp",        furnitureTypeId: "floor_lamp", price: 750, rarity: "rare", description: "A hollowed crystal fitted with a light source. Casts prismatic patterns on the walls." },
        { name: "Root-Wrapped Stone",  price: 200, rarity: "common",  description: "An ordinary stone embraced by ancient roots. Strangely comforting to hold." },
        { name: "Singing Shard",       price: 1500, rarity: "rare",   description: "Hums a single perfect note when touched. No two shards sing the same pitch." },
        { name: "Cavern Diamond",      price: 5000, rarity: "legendary", description: "Found once a year, if you're lucky. The underground pool reflects its light like a second sun." },
      ],
    ),
  ];
}

// Crystal Caverns — 16x16, crystal/stone_dark/lava, extremely dangerous
// No NPCs. Too dangerous for anyone to set up shop.

// Stormwatch Cliffs — 16x14, water (y:0-2), cliff edge (y:3-4), stone plateau below
// Windswept and lonely. Beacon keeps the lighthouse alive.

function stormwatchCliffsNpcs(): NpcState[] {
  return [
    npc(
      "npc_beacon", "Beacon", "Lighthouse Keeper",
      {
        skinTone: "#d0a880",
        hairStyle: "medium_loose",
        hairColour: "#808890",
        outfitTop: "cozy_sweater",
        outfitTopColour: "#c0c040",
        outfitBottom: "trousers",
        outfitBottomColour: "#3a4a4a",
        shoes: "simple_boots",
        shoesColour: "#2a2a1a",
        faceStyle: "kind",
      },
      { tileX: 10, tileY: 8, elevation: 2 },
      "N",
      "guide",
      "stormwatch_cliffs",
      [
        "You made it this far? Not many do. The cliffs test you before you even arrive.",
        "I keep the light burning. Every night, no exceptions. Ships count on me. I don't get to have bad days.",
        "The storms here are honest. They don't pretend to be something they're not. I respect that.",
        "On clear nights — and there aren't many — you can see all the way to Moonlit Harbor. Like looking into a memory.",
        "Professor Aelith and I signal each other sometimes. Light patterns. It's not a code exactly. More like a conversation between two people who like silence.",
      ],
    ),
  ];
}

// Training Grounds — 24x20, sand base, central stone arena ring, dummy yard right side
// The heart of combat learning. Commander Vex drills form, Renna runs the arena,
// Hauk stands ready to spar, and Old Barnaby keeps the dummies in fighting shape.

function trainingGroundsNpcs(): NpcState[] {
  const npcs = [
    npc(
      "npc_commander_vex", "Commander Vex", "Combat Instructor",
      {
        skinTone: "#8a6840",
        hairStyle: "short_spiky",
        hairColour: "#1a1a1a",
        outfitTop: "tunic",
        outfitTopColour: "#8b2020",
        outfitBottom: "trousers",
        outfitBottomColour: "#2a2a2a",
        shoes: "simple_boots",
        shoesColour: "#1a1010",
        faceStyle: "stern",
      },
      { tileX: 10, tileY: 10, elevation: 1 },
      "W",
      "instructor",
      "training_grounds",
      [
        "Stand up straight. Your stance is your foundation — everything else is built on it.",
        "I've trained soldiers, kings, and farmers. The ones who got good? They showed up every day.",
        "Hit the dummies until your arms ache. Then hit them again. Muscle memory doesn't care about talent.",
        "The arena ring is where you prove what you've practiced. Not before. Never before.",
        "Pain is a teacher with no patience and perfect honesty. Listen to it.",
        "I was beaten sixty-three times before I won my first bout. Sixty-three. Each one taught me something.",
      ],
    ),
    npc(
      "npc_renna", "Renna", "Arena Master",
      {
        skinTone: "#d0a070",
        hairStyle: "long_braid",
        hairColour: "#c04010",
        outfitTop: "tunic",
        outfitTopColour: "#c09030",
        outfitBottom: "trousers",
        outfitBottomColour: "#3a3020",
        shoes: "simple_boots",
        shoesColour: "#2a1a10",
        faceStyle: "friendly",
      },
      { tileX: 12, tileY: 7, elevation: 1 },
      "S",
      "arena_master",
      "training_grounds",
      [
        "Welcome to the ring! The rules are simple: fight fair, learn fast, no grudges after.",
        "I manage the duels here. When you're ready for a real challenge, come find me.",
        "The best fighters I've seen? They're the ones who study their opponent before swinging.",
        "This arena has seen a thousand bouts. The stones remember every one. Treat them with respect.",
        "If you want to duel another player, I can arrange it. Official rules, proper countdown, the whole thing.",
        "Nobody leaves my arena worse than they came in. Bruised, maybe. But wiser, always.",
      ],
    ),
    npc(
      "npc_hauk", "Hauk", "Sparring Partner",
      {
        skinTone: "#b08060",
        hairStyle: "short_spiky",
        hairColour: "#6a4a2a",
        outfitTop: "apron",
        outfitTopColour: "#5a5050",
        outfitBottom: "trousers",
        outfitBottomColour: "#3a3a3a",
        shoes: "simple_boots",
        shoesColour: "#2a2020",
        faceStyle: "friendly",
      },
      { tileX: 13, tileY: 11, elevation: 1 },
      "N",
      "quest_giver",
      "training_grounds",
      [
        "Need a sparring partner? I don't hit too hard. Well... I try not to.",
        "Commander Vex broke my nose twice during training. I thanked her both times. Eventually.",
        "The trick to dodging isn't speed — it's reading. Watch the shoulders, not the hands.",
        "I've been sparring since I was twelve. My mother said I'd grow out of it. I'm thirty-four.",
        "If you bring me some decent practice gear, I can show you advanced techniques.",
      ],
    ),
    npc(
      "npc_old_barnaby", "Old Barnaby", "Dummy Master",
      {
        skinTone: "#c09870",
        hairStyle: "short_curly",
        hairColour: "#c0c0c0",
        outfitTop: "apron",
        outfitTopColour: "#6a5a40",
        outfitBottom: "simple_pants",
        outfitBottomColour: "#4a4a3a",
        shoes: "sandals",
        shoesColour: "#3a3020",
        faceStyle: "kind",
      },
      { tileX: 17, tileY: 4, elevation: 0 },
      "E",
      "dummy_master",
      "training_grounds",
      [
        "Careful with those dummies! I carved each one by hand. Well, mostly by hand. And swearing.",
        "The wooden one's for beginners — good for form. The straw one lets you practice combos.",
        "The iron dummy won't break no matter what you throw at it. I reinforced it after... the incident.",
        "Every great warrior started by hitting something that couldn't hit back. No shame in it.",
        "I've been repairing training dummies for forty years. They break; I fix them. Circle of life, innit?",
        "If you want, I can adjust the dummies to simulate different fighting styles. Just say the word.",
      ],
    ),
  ];

  // Explicitly set questIds for quest giver mark computation
  const find = (id: string) => npcs.find(n => n.id === id);
  const vex = find("npc_commander_vex");
  if (vex) vex.questIds = ["q_know_your_stance"];
  const renna = find("npc_renna");
  if (renna) renna.questIds = ["q_ring_ready"];
  const hauk = find("npc_hauk");
  if (hauk) hauk.questIds = ["q_first_strike", "q_sparring_hauk"];
  const barnaby = find("npc_old_barnaby");
  if (barnaby) barnaby.questIds = ["q_dummy_duty"];

  return npcs;
}

// ─── Zone NPC Registry ──────────────────────────────────────────────────────

const ZONE_NPC_FACTORIES: Record<string, () => NpcState[]> = {
  ember_lane:          emberLaneNpcs,
  hearthstone_square:  hearthstoneSquareNpcs,
  lantern_market:      lanternMarketNpcs,
  verdant_park:        verdantParkNpcs,
  ironforge_district:  ironforgeDistrictNpcs,
  moonlit_harbor:      moonlitHarborNpcs,
  whisperwood:         whisperwoodNpcs,
  skyreach_terrace:    skyreachTerraceNpcs,
  ashen_peaks:         ashenPeaksNpcs,
  roothollow:          roothollowNpcs,
  crystal_caverns:     () => [],
  stormwatch_cliffs:   stormwatchCliffsNpcs,
  training_grounds:    trainingGroundsNpcs,
};

/** Returns fresh NPC instances for the given zone. */
export function getZoneNpcs(zoneId: string): NpcState[] {
  const factory = ZONE_NPC_FACTORIES[zoneId];
  return factory ? factory() : [];
}
